import React, { useEffect, useState, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Button } from '@app/components';
import { useSelector } from 'react-redux';
import { getAllActiveSubjects } from '@app/services/subject';
import { getLearningPathwaysWithPagination } from '@app/services/learningPathway';
import Select from "react-select";
import { getAllActiveFranchise } from '@app/services/franchise';
import UpdatePathwayNameModal from './UpdatePathwayNameModal';
import UpdatePathwayStatusModal from './UpdatePathwayStatusModal';
import moment from 'moment';
import { encodeId } from '@app/helpers/base64url';

const ListLearningPathways = () => {

    const [pathways, setPathways] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams()
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(searchParams.get('perPage') ?? 10);
    const [currentPage, setCurrentPage] = useState(searchParams.get('page') ?? 1);
    const statuses = [{value:'active', label:'Active'}, {value:'inactive', label:'Inactive'}, {value:'draft', label:'Draft'}]
    const [franchises, setFranchises] = useState([]);
    const selectRef = useRef(null);
    const canAccess = useSelector((state: any) => state.auth.permissions['learning-pathway']);
    const role = useSelector((state: any) => state.auth.selectedRole.slug);
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [showStatus, setShowStatus] = useState(false);
    const handleStatusClose = () => setShowStatus(false);
    const handleStatusShow = () => setShowStatus(true);
    const [updateNamePayload, setUpdateNamePayload] = useState({id: '', name: ''});
    const [updateStatusPayload, setUpdateStatusPayload] = useState({id: '', status: ''});

    const customStyle: TableStyles = {
        head: {
            style: {
                color: "#5E5873",
                fontSize: "13px",
                fontWeight: 900
            },
        },
        headCells: {
            style: {
                borderColor: "#E9ECEF",
                textTransform: "uppercase",
                letterSpacing: "1px",
            },
        },
        headRow: {
            style: {
                backgroundColor: "#F7F7F8",
                minHeight: "44px",
                fontWeight: 500,
            },
        },
    };
      
    useEffect(() => {
        getAllLearningPathwayData()
    },[searchParams]);

    useEffect(() => {
        getAllActiveFranchiseData();
        getAllSubjectsData();
    },[]);

    const getAllSubjectsData = async () => {
        try {
            const subjectsResp = await getAllActiveSubjects();
            if (subjectsResp.success === true) {
                setSubjects(subjectsResp.collections);
            } else {
                toast.error(subjectsResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getAllLearningPathwayData = async () => {
        try {
            setLoading(true);
            const resp = await getLearningPathwaysWithPagination(searchParams);
            if (resp.success === true) {
                setLoading(false);
                setPathways(resp?.collections?.data);
                setTotalRows(resp?.collections?.pagination?.total);
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getAllActiveFranchiseData = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) {
                setFranchises([{id: 0, name: 'FCL Default'}, ...resp?.collections]);
                // setFranchises(resp?.collections);
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        }
    };

    const columns = [
        {
            name: 'Franchise',
            selector: (row: { franchise_name: string; }) => row.franchise_name ?? '---',
            cell: (row: { franchise_name: string; franchise_id: number; }) => (
                row.franchise_id === 0 ? <b>FCL Default</b> : row.franchise_name ?? '---'
            ),
            sortable: true,
            sortField: 'franchise_name',
        },
        {
            name: 'Subject',
            selector: (row: { subject_name: string; }) => row.subject_name,
            sortable: true,
            sortField: 'subject_name',
        },
        {
            name: 'Pathway Name',
            selector: (row: { name: string; }) => row.name,
            cell: (row: { name: string; }) => (
                <a href='#' onClick={ (e) => { handleUpdateName(e, row) }} data-toggle="tooltip" title="Update Pathway Name">
                    { row.name ?? '---' }
                </a>
            ),
            sortable: true,
            sortField: 'name',
            wrap: true,
        },
        {
            name: 'Student(s) Assigned',
            selector: (row: { student_assigned: string; }) => row.student_assigned,
            sortable: true,
            sortField: 'student_assigned',
        },
        {
            name: 'Status',
            selector: (row: { status: string;}) => row.status,
            cell: (row: { status: string; }) => (
                <a href='#' onClick={ (e) => {  handleUpdateStatus(e, row) }} data-toggle="tooltip" title="Update status">
                    { row.status !== null ? 
                        row.status === 'active' ? <span className="badge bg-success">Active</span> : (row.status === 'draft' ? <span className="badge bg-warning">Draft</span> : <span className="badge bg-danger">{ row.status}</span>)
                    : '---'}
                </a>
            ),
            sortable: true,
            sortField: 'status',
        },
        {
            name: 'Updated At',
            selector: (row: { updated_at: string; }) => row.updated_at,
            cell: (row: { updated_at: string; }) => (
                row.updated_at ? moment(row.updated_at).format('DD/MM/YYYY HH:mm') : '---'
            ),
            sortable: true,
            sortField: 'updated_at',
        },
        {  
            name: 'Action',
            cell: (row: { id: string; franchise_id: number; }) => (
              <>
                { ((canAccess?.edit && row.franchise_id == 0 && (role === 'admin' || role === 'super-admin')) || (row.franchise_id > 0 && canAccess?.edit)) &&
                    <Link
                        to={`/learning-pathway/edit/${encodeId(row.id)}`}
                        role="button"
                        state={{ id: row.id }}
                        title="Edit Pathway"
                    >
                        <i className="fas fa-pen mr-2" />
                    </Link>
                }
                { canAccess?.view &&
                    <Link
                        to={`/learning-pathway/details/${encodeId(row.id)}`}
                        role="button"
                        state={{ id: row.id }}
                        title="View Pathway Details"
                    >
                        <i className="fas fa-eye mr-2" />
                    </Link>
                }
                { canAccess?.copy &&
                    <Link
                        to={`/learning-pathway/copy/${encodeId(row.id)}`}
                        role="button"
                        state={{ id: row.id }}
                        title="Copy Pathway"
                    >
                        <i className="fas fa-copy mr-2" />
                    </Link>
                }
              </>
            ),
        },
    ];

    const handleUpdateName= (e, row) => {
        e.preventDefault();
        if(row.franchise_name == null && role !== 'admin' && role !== 'super-admin') {
            return toast.error('You don’t have permission to update the name of this pathway')
        }
        handleShow();
        setUpdateNamePayload(row)
    }

    const handleUpdateStatus = async(e, row) => {
        e.preventDefault();
        if(row.franchise_name == null && role !== 'admin' && role !== 'super-admin') {
            return toast.error('You don’t have permission to update the status of this pathway.')
        }
        if(row.student_assigned !== '0') {
            return toast.error('Cannot update status of a pathway that has a student assigned to it')
        }
        handleStatusShow();
        setUpdateStatusPayload(row)
    }

    const handlePageChange = page => {
        searchParams.set('page', page)
        setSearchParams(searchParams)
        
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        searchParams.set('perPage', newPerPage)
        setSearchParams(searchParams)
        
        setPerPage(newPerPage);
    };

    const handleSort = async (column, sortDirection) => {
        searchParams.set('sortField', column.sortField)
        searchParams.set('sortDirection', sortDirection)
        setSearchParams(searchParams)
    };

    const handleResetFilter = async () => {
        selectRef.current.setValue('');
        searchParams.delete('name')
        searchParams.delete('franchiseId')
        searchParams.delete('subjectId')
        searchParams.delete('status')
        setSearchParams(searchParams)  
    };

     const onChangeFranchise = (option) => {
        searchParams.set('franchiseId', option == null ? '' : option.id )
        setSearchParams(searchParams)
    };
    
    return (
        <div>
            <div>
                {/* Content Header (Page header) */}
                <section className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1>Learning Pathways</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                                    <li className="breadcrumb-item active">Learning Pathways</li>
                                </ol>
                            </div>
                        </div>
                    </div>{/* /.container-fluid */}
                </section>
                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div style={{ marginBottom: 10 }} className="row">
                                    
                                </div>
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Learning Pathways</h3>
                                        <div className="col-lg-12 text-right">
                                            { canAccess?.create && <Link className="btn btn-primary mr-2" to="/learning-pathway/create" > <i className="fas fa-plus"/> Create New Learning Pathway </Link> }
                                            {/* { canAccess?.reorder && <Link className="btn btn-secondary" to="/modules/reorder" ><i className="fas fa-sort mr-2"/> Reorder Modules </Link> } */}
                                        </div>
                                    </div>
                                    {/* /.card-header */}
                                    <div className="card-body">
                                        <div className="row mb-2">
                                            <div className="col-sm-3">
                                                <Select
                                                    ref={selectRef}
                                                    name="franchiseId"
                                                    placeholder="Search By Franchise"
                                                    options={franchises}
                                                    getOptionValue={(option)=>`${option['id']}`}
                                                    getOptionLabel={(option)=> option['name'] }
                                                    onChange={ (option) => {onChangeFranchise(option)}}
                                                    value={franchises.filter(function(option) {
                                                        if (searchParams.get('franchiseId') === "" || searchParams.get('franchiseId') === null) return false; // no default selection for empty or missing
                                                        return Number(option.id) === Number(searchParams.get('franchiseId'));
                                                    })}
                                                    isClearable={true}
                                                    formatOptionLabel={(option) => (
                                                        <div style={{ fontWeight: option.id == 0 ? 700 : '' }}>
                                                            {option.name}
                                                        </div>
                                                    )}
                                                />
                                            </div>
                                            <div className="col-sm-2">
                                                <select
                                                    className="form-control"
                                                    name="subjectId"
                                                    onChange={(e) => {  searchParams.set('subjectId', e.target.value); setSearchParams(searchParams)}}
                                                    value={searchParams.get('subjectId') ?? ''}
                                                >
                                                    <option value="">Search By Subject</option>
                                                    {subjects.map((obj) => (
                                                        <option key={obj.id} value={obj.id}>
                                                            {obj.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-sm-2">
                                                <input type="text" name='name' className="form-control" value={searchParams.get('name') ?? ''} onChange={(e) => { searchParams.set('name', e.target.value); setSearchParams(searchParams)}} placeholder="Search By Pathway Name" />
                                            </div>
                                            <div className="col-sm-2">
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    onChange={(e) => {  searchParams.set('status', e.target.value); setSearchParams(searchParams)}}
                                                    value={searchParams.get('status') ?? ''}
                                                >
                                                    <option value="">Search By Status</option>
                                                    {statuses.map((obj) => (
                                                        <option key={obj.value} value={obj.value}>
                                                            {obj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='col-sm-2'>
                                                <Button type="submit" isLoading={isLoading} onClick={handleResetFilter} className="btn btn-warning">Reset Filter</Button>
                                            </div>
                                        </div>
                                        
                                        <DataTable
                                            columns={columns}
                                            data={pathways}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            paginationDefaultPage={Number(currentPage)}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            paginationPerPage={Number(perPage)}
                                            paginationRowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                                            progressPending={isLoading}
                                            sortServer
                                            onSort={handleSort}
                                            // paginationComponent
                                            // selectableRows
                                            customStyles={customStyle}
                                        />
                                    </div>
                                    {/* /.card-body */}
                                </div>
                            </div>
                            {/* /.col */}
                        </div>
                        {/* /.row */}
                    </div>
                    {/* /.container-fluid */}
                </section>
                {/* /.content */}
                <UpdatePathwayNameModal
                    show={show}
                    handleClose={handleClose}
                    callGetAllLearningPathwayData={getAllLearningPathwayData}
                    payload={updateNamePayload}
                />
                <UpdatePathwayStatusModal
                    show={showStatus}
                    handleClose={handleStatusClose}
                    callGetAllLearningPathwayData={getAllLearningPathwayData}
                    payload={updateStatusPayload}
                />
            </div>
        </div>
    )
};

export default ListLearningPathways;